import React, { useEffect, useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import { Button } from '../shared/Button';
interface VideoPlayerScreenProps {
  navigateTo: (screen: string) => void;
  workout?: {
    id: string;
    title: string;
    duration: string;
    type: string;
    videoUrl: string;
    thumbnail: string;
  };
}
export const VideoPlayerScreen = ({
  navigateTo,
  workout
}: VideoPlayerScreenProps) => {
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLandscape, setIsLandscape] = useState(false);
  const playerRef = useRef<ReactPlayer>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  // Default workout if none provided
  const defaultWorkout = {
    id: '1',
    title: '15-min Heart Pump',
    duration: '15',
    type: 'Cardio',
    videoUrl: 'https://www.youtube.com/watch?v=ml6cT4AZdqI',
    thumbnail: 'https://images.unsplash.com/photo-1599058917765-a780eda07a3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1469&q=80'
  };
  const currentWorkout = workout || defaultWorkout;
  // Check orientation on mount and when it changes
  useEffect(() => {
    const checkOrientation = () => {
      const landscape = window.matchMedia('(orientation: landscape)').matches;
      setIsLandscape(landscape);
      if (landscape && videoContainerRef.current) {
        // Request fullscreen when in landscape mode
        if (videoContainerRef.current.requestFullscreen) {
          videoContainerRef.current.requestFullscreen().catch(err => {
            console.error('Error attempting to enable fullscreen:', err);
          });
        }
      }
    };
    // Check initial orientation
    checkOrientation();
    // Add event listener for orientation changes
    const mediaQuery = window.matchMedia('(orientation: landscape)');
    const handleOrientationChange = () => checkOrientation();
    // Use the correct event based on browser support
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleOrientationChange);
    } else {
      // For older browsers
      mediaQuery.addListener(handleOrientationChange);
    }
    // Handle fullscreen change/exit
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isLandscape) {
        // If exiting fullscreen while still in landscape, we might want to handle this
        // For example, you could force portrait orientation or show a message
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      // Clean up event listeners
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleOrientationChange);
      } else {
        mediaQuery.removeListener(handleOrientationChange);
      }
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isLandscape]);
  // Format time to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const handleProgress = (state: {
    playedSeconds: number;
    played: number;
  }) => {
    setCurrentTime(state.playedSeconds);
    setProgress(state.played * 100);
  };
  const handleDuration = (duration: number) => {
    setDuration(duration);
  };
  const togglePlay = () => {
    setPlaying(!playing);
  };
  const handleBackClick = () => {
    // Exit fullscreen if active before navigating
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
    navigateTo('workouts');
  };
  const handleEndClick = () => {
    // Exit fullscreen if active before navigating
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
    navigateTo('workouts');
  };
  return <div className="flex flex-col h-full min-h-screen bg-black text-white" data-id="element-1548">
      {/* Header - only show in portrait mode */}
      {!isLandscape && <div className="p-4 flex items-center" data-id="element-1549">
          <button onClick={handleBackClick} className="text-white mr-4" data-id="element-1550">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1551">
              <path d="m15 18-6-6 6-6" data-id="element-1552"></path>
            </svg>
          </button>
          <h1 className="text-xl font-bold flex-1 text-center" data-id="element-1553">
            {currentWorkout.title}
          </h1>
          <div className="w-8" data-id="element-1554"></div> {/* Empty space for alignment */}
        </div>}

      {/* Video Player */}
      <div ref={videoContainerRef} className={`relative ${isLandscape ? 'fixed inset-0 z-50' : 'flex-1 flex flex-col'}`} data-id="element-1555">
        <div className={`relative ${isLandscape ? 'w-full h-full' : 'w-full flex-1'}`} data-id="element-1556">
          <div className={`${isLandscape ? 'w-full h-full' : 'relative w-full h-full overflow-hidden'}`} style={{
          // In portrait mode, zoom in the video for a vertical preview
          // This creates a cropped center view that fills the vertical space
          ...(isLandscape ? {} : {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          })
        }} data-id="element-1557">
            <ReactPlayer ref={playerRef} url={currentWorkout.videoUrl} width={isLandscape ? '100%' : 'auto'} height={isLandscape ? '100%' : '100%'} style={{
            // In portrait mode, scale up the video to fill the vertical space
            ...(isLandscape ? {} : {
              minHeight: '100%',
              minWidth: '100%',
              transform: 'scale(1.2)',
              objectFit: 'cover'
            })
          }} playing={playing} controls={isLandscape} onProgress={handleProgress} onDuration={handleDuration} config={{
            youtube: {
              playerVars: {
                showinfo: 0,
                controls: isLandscape ? 1 : 0,
                modestbranding: 1
              }
            }
          }} data-id="element-1558" />
            {/* Timer overlay - only show in portrait mode */}
            {!isLandscape && <div className="absolute top-2 right-2 bg-black bg-opacity-70 px-3 py-1 rounded-full text-sm z-10" data-id="element-1559">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>}
            {/* Rotate device hint - only show in portrait mode */}
            {!isLandscape && <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 px-4 py-2 rounded-full text-sm z-10 flex items-center" data-id="element-1560">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-1561">
                  <path d="M4 17h16" data-id="element-1562"></path>
                  <path d="M4 17l3-3" data-id="element-1563"></path>
                  <path d="M20 17l-3-3" data-id="element-1564"></path>
                  <path d="M7 14v6" data-id="element-1565"></path>
                  <path d="M17 14v6" data-id="element-1566"></path>
                  <path d="M14 14v6" data-id="element-1567"></path>
                  <path d="M10 14v6" data-id="element-1568"></path>
                </svg>
                Rotate for fullscreen
              </div>}
          </div>
        </div>
        {/* Workout Info - only show in portrait mode */}
        {!isLandscape && <div className="bg-black p-6 text-center" data-id="element-1569">
            <div className="mb-2" data-id="element-1570">
              <span className="inline-block px-3 py-1 bg-blue-600 rounded-full text-sm font-medium" data-id="element-1571">
                {currentWorkout.type}
              </span>
              <span className="inline-block ml-2 px-3 py-1 bg-gray-700 rounded-full text-sm font-medium" data-id="element-1572">
                {currentWorkout.duration} min
              </span>
            </div>
            {/* Timer Display */}
            <div className="flex justify-between items-center my-6" data-id="element-1573">
              <div className="text-center" data-id="element-1574">
                <div className="text-5xl font-bold" data-id="element-1575">
                  {formatTime(duration - currentTime)}
                </div>
                <div className="text-gray-400 mt-1" data-id="element-1576">Time left</div>
              </div>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-gray-700 h-1 rounded-full mb-8" data-id="element-1577">
              <div className="bg-blue-500 h-1 rounded-full" style={{
            width: `${progress}%`
          }} data-id="element-1578"></div>
            </div>
            {/* Controls */}
            <div className="flex justify-between" data-id="element-1579">
              <Button onClick={togglePlay} variant="secondary" className="w-40 py-3 bg-gray-800 hover:bg-gray-700" data-id="element-1580">
                {playing ? 'Pause' : 'Play'}
              </Button>
              <Button onClick={handleEndClick} variant="outline" className="w-40 py-3 border-white text-white hover:bg-gray-800" data-id="element-1581">
                End
              </Button>
            </div>
          </div>}
      </div>
    </div>;
};